import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../components/legal/LegalLayout";

const LAST_UPDATED = "November 19, 2025";

export default function Privacy() {
    useEffect(() => {
        document.title = "Privacy Policy — BetweenUs";
    }, []);
    return (
        <LegalLayout
            eyebrow="Legal"
            title="Privacy Policy"
            updatedAt={LAST_UPDATED}
            siblingLabel="Terms & Conditions"
            siblingTo="/terms"
            testIdRoot="privacy"
        >
            <section data-testid="privacy-intro">
                <h2>1. Introduction</h2>
                <p>
                    This Privacy Policy explains how Tylegn Gaming and
                    Entertainment, LLC (“Tylegn”, “we”, “us”, “our”)
                    collects, uses, shares, and protects personal
                    information when you use the BetweenUs mobile
                    application and related services (“BetweenUs”, “the
                    App”).
                </p>
                <p>
                    BetweenUs is a communication and guidance tool that
                    helps people better understand each other’s
                    perspectives. Because the App may involve sensitive
                    conversations, we take privacy seriously and aim to be
                    transparent about our practices.
                </p>
                <p>
                    By using the App, you agree to the collection and use
                    of information in accordance with this Policy. You
                    should also review our{" "}
                    <Link to="/terms" data-testid="privacy-link-terms">
                        Terms and Conditions
                    </Link>
                    .
                </p>
            </section>

            <section>
                <h2>2. Information We Collect</h2>
                <p>
                    The specific data we collect will depend on how you
                    use BetweenUs and your device settings. Common
                    categories include:
                </p>

                <h3>Account Information</h3>
                <ul>
                    <li>Name or display name</li>
                    <li>Email address or login identifier</li>
                    <li>Password or authentication tokens</li>
                </ul>

                <h3>Profile and Relationship Context</h3>
                <ul>
                    <li>
                        Relationship type (e.g., roommates, couple,
                        parent/teen)
                    </li>
                    <li>
                        Basic demographic information you choose to share
                        (e.g., age range, role such as parent or teen)
                    </li>
                </ul>

                <h3>Content You Provide</h3>
                <ul>
                    <li>Text entries, messages, and form responses</li>
                    <li>
                        Voice notes or audio recordings you submit for
                        summarization or analysis
                    </li>
                    <li>
                        Reactions, selections, and checkboxes in the App
                        (for example, which proposed solution you prefer)
                    </li>
                </ul>

                <h3>Usage Data</h3>
                <ul>
                    <li>Device type and operating system</li>
                    <li>App version</li>
                    <li>
                        Log data such as timestamps, feature usage, and
                        interaction patterns
                    </li>
                    <li>Crash logs and diagnostic information</li>
                </ul>

                <h3>Approximate Location and Region</h3>
                <ul>
                    <li>
                        Country or region derived from IP address or app
                        store region, used for legal and analytics purposes
                    </li>
                </ul>
                <p>
                    We do not require precise GPS location to provide core
                    conflict‑resolution features.
                </p>

                <h3>Payment Information (if applicable)</h3>
                <ul>
                    <li>
                        Subscription or purchase information provided by
                        the app store (e.g., transaction ID, plan type,
                        billing status)
                    </li>
                </ul>
                <p>
                    Tylegn does not receive full credit card numbers;
                    those are processed by the app store or payment
                    providers.
                </p>
            </section>

            <section>
                <h2>3. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul>
                    <li>
                        Provide, operate, and maintain BetweenUs and its
                        features.
                    </li>
                    <li>
                        Process your input (text or audio) to generate
                        summaries, suggestions, and options using AI models.
                    </li>
                    <li>
                        Help you track conflicts, agreements, and history
                        within the App.
                    </li>
                    <li>
                        Improve the App, including debugging, analytics,
                        and research into feature usage.
                    </li>
                    <li>
                        Customize your experience (for example, suggested
                        templates or topics).
                    </li>
                    <li>
                        Communicate with you about updates, changes, or
                        important notices.
                    </li>
                    <li>
                        Detect, prevent, and address technical or security
                        issues.
                    </li>
                    <li>Comply with legal obligations.</li>
                </ul>
                <p>
                    We may aggregate or de‑identify data so that it no
                    longer identifies you; we may use such aggregated data
                    for analytics, research, and improvement.
                </p>
            </section>

            <section>
                <h2>4. AI and Third‑Party Services</h2>
                <p>
                    BetweenUs may rely on third‑party AI and infrastructure
                    providers to process text and audio and to host or
                    analyze data.
                </p>
                <p>Where we do so:</p>
                <ul>
                    <li>
                        We strive to enter into agreements that require
                        these providers to handle data securely and in
                        accordance with applicable law.
                    </li>
                    <li>
                        We only share data with them to the extent
                        reasonably necessary to operate and improve the
                        App.
                    </li>
                    <li>
                        We do not authorize them to use your personal
                        information for their own direct marketing.
                    </li>
                </ul>
                <p>
                    We may also use analytics services to understand App
                    performance and usage (for example, which screens are
                    used most). These services typically collect
                    pseudonymous data, such as device identifiers or usage
                    events.
                </p>
            </section>

            <section>
                <h2>5. Legal Bases (if applicable)</h2>
                <p>
                    If laws like the GDPR or UK GDPR apply, we process
                    personal data under the following legal bases:
                </p>
                <ul>
                    <li>
                        <strong>Contract:</strong> To provide BetweenUs
                        and perform our agreement with you.
                    </li>
                    <li>
                        <strong>Legitimate Interests:</strong> To improve
                        the App, prevent abuse, and secure our systems,
                        provided these interests are not overridden by
                        your rights.
                    </li>
                    <li>
                        <strong>Consent:</strong> Where required (for
                        example, certain analytics or marketing
                        communications).
                    </li>
                </ul>
                <p>
                    You may have rights such as access, correction,
                    deletion, restriction, portability, and objection,
                    subject to local law.
                </p>
            </section>

            <section>
                <h2>6. Sharing Your Information</h2>
                <p>We may share your information with:</p>
                <ul>
                    <li>
                        <strong>Service Providers:</strong> Vendors that
                        help us operate BetweenUs (hosting, analytics, AI
                        processing, customer support, email services).
                    </li>
                    <li>
                        <strong>Business Transfers:</strong> In connection
                        with mergers, acquisitions, or sale of assets,
                        subject to appropriate confidentiality.
                    </li>
                    <li>
                        <strong>Legal and Safety:</strong> Where required
                        by law, subpoena, court order, or to protect the
                        rights, safety, or property of you, Tylegn, or
                        others.
                    </li>
                </ul>
                <p>
                    We do not sell your personal data in the sense
                    commonly defined by major privacy laws (for example,
                    the CCPA’s definition of a “sale”), and we do not
                    share your conflicts or conversation content for
                    third‑party marketing.
                </p>
            </section>

            <section>
                <h2>7. Parents, Teens, and Sensitive Topics</h2>
                <p>
                    BetweenUs is intended primarily for adults. Parents or
                    legal guardians may choose to use the App to
                    facilitate conversations with their teens.
                </p>
                <p>If you use the App as a parent or guardian:</p>
                <ul>
                    <li>
                        You are responsible for ensuring that your teen’s
                        participation complies with applicable laws and
                        any school or custody agreements.
                    </li>
                    <li>
                        You agree not to use the App for secret
                        surveillance or recording; its purpose is guided,
                        structured conversation.
                    </li>
                </ul>
                <p>
                    We do not knowingly allow children under 13 to create
                    their own independent accounts. If we learn that we
                    have collected personal information from a child
                    without proper consent, we will take steps to delete
                    it.
                </p>
                <p>
                    Because the App may involve sensitive topics
                    (relationship conflict, family conflict, stress), we
                    treat this content as highly confidential and do not
                    use it for targeted advertising.
                </p>
            </section>

            <section>
                <h2>8. Data Retention</h2>
                <p>
                    We keep personal information only for as long as
                    reasonably necessary to:
                </p>
                <ul>
                    <li>Provide BetweenUs and maintain your account,</li>
                    <li>Comply with legal obligations,</li>
                    <li>Resolve disputes, and</li>
                    <li>Enforce our agreements.</li>
                </ul>
                <p>
                    You may request that we delete your account and
                    associated content. We may retain certain data in
                    backup or log files for a limited period where
                    required for security, legal, or operational reasons.
                </p>
            </section>

            <section>
                <h2>9. Security</h2>
                <p>
                    Tylegn uses reasonable administrative, technical, and
                    physical safeguards to protect your information from
                    loss, theft, and unauthorized access or disclosure.
                </p>
                <p>
                    However, no system is completely secure. We cannot
                    guarantee absolute security. You are responsible for
                    keeping your login credentials confidential and for
                    using the App in a safe environment.
                </p>
            </section>

            <section>
                <h2>10. Your Rights and Choices</h2>
                <p>Depending on your location, you may have rights such as:</p>
                <ul>
                    <li>
                        <strong>Access</strong> – Request a copy of your
                        personal information.
                    </li>
                    <li>
                        <strong>Correction</strong> – Request correction
                        of inaccurate information.
                    </li>
                    <li>
                        <strong>Deletion</strong> – Request deletion of
                        your information, subject to legal obligations.
                    </li>
                    <li>
                        <strong>Restriction</strong> – Request that we
                        limit how we use your data.
                    </li>
                    <li>
                        <strong>Portability</strong> – Request a
                        structured, machine‑readable copy of certain data.
                    </li>
                    <li>
                        <strong>Objection</strong> – Object to certain
                        uses, such as direct marketing where applicable.
                    </li>
                </ul>
                <p>You can also:</p>
                <ul>
                    <li>
                        Update some information directly within the App
                        (if available).
                    </li>
                    <li>Request account deletion.</li>
                    <li>Opt out of certain emails or notifications.</li>
                </ul>
                <p>
                    To exercise your rights, contact us at{" "}
                    <a href="mailto:privacy@tylegn.com">
                        privacy@tylegn.com
                    </a>
                    . We may need to verify your identity before
                    responding.
                </p>
            </section>

            <section>
                <h2>11. International Transfers</h2>
                <p>
                    If you are located outside the United States, your
                    information may be transferred to, stored in, and
                    processed in the United States or other countries
                    that may have different data protection laws.
                </p>
                <p>
                    Where required by law, we will take steps to ensure
                    appropriate safeguards are in place (for example,
                    standard contractual clauses).
                </p>
            </section>

            <section>
                <h2>12. Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time.
                    When we do, we will update the “Last updated” date
                    and may provide additional notice (such as an in‑app
                    message).
                </p>
                <p>
                    Your continued use of BetweenUs after changes take
                    effect constitutes your acceptance of the updated
                    Policy.
                </p>
            </section>

            <section>
                <h2>13. Contact Us</h2>
                <p>
                    If you have questions about this Privacy Policy or
                    our data practices, contact:
                </p>
                <address>
                    <strong>Tylegn Gaming and Entertainment, LLC</strong>
                    <br />
                    Email:{" "}
                    <a href="mailto:privacy@tylegn.com">
                        privacy@tylegn.com
                    </a>
                    <br />
                    Mailing Address: PO Box 3118, Hailey, ID 83333
                </address>
            </section>

            <p className="legal-cross-link">
                See also:{" "}
                <Link to="/terms" data-testid="privacy-link-terms-bottom">
                    Terms & Conditions
                </Link>
                {" "}·{" "}
                <Link to="/eula" data-testid="privacy-link-eula">
                    EULA
                </Link>
                .
            </p>
        </LegalLayout>
    );
}
